import React from "react";

/**
 * Local replacement for `@blumentechorg/blumenjs/runtime`.
 *
 * That package is private (GitHub Packages, org `blumentechorg`) and
 * this deployment has no credentials for it. Everything this app
 * actually uses from it is reimplemented here, scoped exactly to the
 * real call sites in app/shared/RouterContext.tsx and
 * node-ssr/server.ts — nothing speculative. See PR notes for the
 * verification behind each piece.
 */

// ─── Route matching ─────────────────────────────────────────────

export interface RouteDef {
	path: string;
	pattern: RegExp;
	keys: string[];
	component: React.ComponentType<any>;
	loading?: React.ComponentType<any>;
}

export interface RouteMatch {
	component: React.ComponentType<any>;
	params: Record<string, string>;
	loading?: React.ComponentType<any>;
}

/**
 * Matches a path against the generated route table. Returns the
 * matched route's component/loading plus any dynamic params
 * extracted via the route's regex capture groups + key names.
 */
export function matchRoute(path: string, routes: RouteDef[]): RouteMatch | null {
	for (const route of routes) {
		const result = route.pattern.exec(path);
		if (!result) continue;

		const params: Record<string, string> = {};
		route.keys.forEach((key, i) => {
			params[key] = decodeURIComponent(result[i + 1] ?? "");
		});

		return { component: route.component, params, loading: route.loading };
	}
	return null;
}

// ─── Client-side prefetch cache ─────────────────────────────────
//
// No <Link> component exists anywhere in this app (every nav link is
// a plain <a href>), so nothing ever populates a prefetch cache today
// — this always being a miss matches real current behavior exactly.
// Kept as a real (currently-empty) cache, not hardcoded to null, so a
// future prefetch-on-hover implementation can populate it without
// touching RouterContext.tsx.

const prefetchCache = new Map<string, unknown>();

export function consumeCached(path: string): unknown {
	if (!prefetchCache.has(path)) return undefined;
	const value = prefetchCache.get(path);
	prefetchCache.delete(path);
	return value;
}

export function cachePrefetch(path: string, data: unknown): void {
	prefetchCache.set(path, data);
}

// ─── Client-side <head> metadata updates ────────────────────────
//
// Applied after a client-side navigation so the tab title/description
// reflect the new page without a full reload.

export function applyMetadata(metadata: Record<string, any> | undefined | null): void {
	if (!metadata || typeof document === "undefined") return;

	if (typeof metadata.title === "string") {
		document.title = metadata.title;
	}
	if (typeof metadata.description === "string") {
		upsertMetaTag("description", metadata.description);
	}
	if (typeof metadata.keywords === "string") {
		upsertMetaTag("keywords", metadata.keywords);
	}
}

function upsertMetaTag(name: string, content: string): void {
	let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
	if (!tag) {
		tag = document.createElement("meta");
		tag.setAttribute("name", name);
		document.head.appendChild(tag);
	}
	tag.setAttribute("content", content);
}

// ─── Loading fallback ────────────────────────────────────────────

export const DefaultLoading: React.FC = () => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minHeight: "40vh",
		}}
	>
		<div
			style={{
				width: 32,
				height: 32,
				borderRadius: "50%",
				border: "3px solid rgba(46,204,113,0.15)",
				borderTopColor: "#2ecc71",
				animation: "blumen-spin 0.8s linear infinite",
			}}
		/>
		<style>{`@keyframes blumen-spin { to { transform: rotate(360deg); } }`}</style>
	</div>
);

// ─── Error boundary ──────────────────────────────────────────────

interface ErrorBoundaryState {
	hasError: boolean;
}

export class BlumenErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
	constructor(props: { children: React.ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: unknown, info: React.ErrorInfo): void {
		console.error("Uncaught error while rendering page:", error, info);
	}

	render() {
		if (this.state.hasError) {
			return (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						minHeight: "50vh",
						padding: "2rem",
						textAlign: "center",
						gap: "1rem",
					}}
				>
					<h1 style={{ fontSize: "1.25rem", fontWeight: 700 }}>Something went wrong</h1>
					<p style={{ opacity: 0.6, fontSize: "0.9rem" }}>
						Please try reloading the page.
					</p>
					<button
						type="button"
						onClick={() => window.location.reload()}
						style={{
							padding: "0.6rem 1.25rem",
							borderRadius: "0.6rem",
							border: "none",
							backgroundColor: "#166534",
							color: "#fff",
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						Reload
					</button>
				</div>
			);
		}
		return this.props.children;
	}
}

// ─── Server actions registry ─────────────────────────────────────
//
// No page in this app currently calls /_blumen/action or registers
// an action anywhere (verified: no registerAction/defineAction call
// site exists in the codebase), so this registry is correctly empty
// today. It's real infrastructure, not a stub returning fake success
// — register a handler here if/when a page needs one.

type ActionHandler = (input: any) => Promise<any> | any;

const actionRegistry = new Map<string, ActionHandler>();

export function registerAction(name: string, handler: ActionHandler): void {
	actionRegistry.set(name, handler);
}

export function getRegisteredActions(): string[] {
	return Array.from(actionRegistry.keys());
}

export async function executeAction(name: string, input: any): Promise<{ success: boolean; data?: any; error?: string }> {
	const handler = actionRegistry.get(name);
	if (!handler) {
		return { success: false, error: `Unknown action: ${name}` };
	}
	try {
		const data = await handler(input);
		return { success: true, data };
	} catch (error: any) {
		return { success: false, error: error?.message || "Action failed" };
	}
}
